/**
 * World Space Utilities
 * 
 * Calculates absolute positions for all frames in a 2D world space.
 * This enables position-based navigation instead of index-based navigation.
 */

import type { OffsetMap } from '@/types/offsetMap'
import { extractOffsetsFromManifest, type Manifest } from './calculateOffsetMapFromManifest'

export interface FrameWorldPosition {
  frameIndex: number
  worldX: number
  worldY: number
}

/**
 * Calculate world space positions for all frames (with manifest fallback)
 * 
 * Uses manifest.json if available (faster, smaller data), otherwise falls back to offset_map.
 * Automatically detects which source has usable data.
 * 
 * @param offsetMap - Pre-calculated offset map (fallback if no manifest)
 * @param totalFrames - Total number of frames in dataset
 * @param manifest - Optional manifest with quality-filtered offsets (preferred if available)
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @returns Array of world positions for each frame
 */
export function calculateFrameWorldPositions(
  offsetMap: OffsetMap,
  totalFrames: number,
  manifest?: Manifest,
  frameWidth?: number,
  frameHeight?: number
): FrameWorldPosition[] {
  // Prefer manifest if available (faster, smaller data)
  if (manifest && manifest.frames && manifest.frames.length > 0) {
    console.log('[WorldSpace] Using manifest.json for position calculation')
    return calculateFrameWorldPositionsFromManifest(manifest, frameWidth, frameHeight)
  }
  
  // Fall back to offset_map approach
  console.log('[WorldSpace] Using offset_map.json for position calculation')
  return calculateFrameWorldPositionsFromOffsetMap(offsetMap, totalFrames, frameWidth, frameHeight)
}

/**
 * Calculate world space positions from offset_map.json (legacy approach)
 * 
 * @param offsetMap - Pre-calculated offset map with sequential offsets
 * @param totalFrames - Total number of frames in dataset
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @returns Array of world positions for each frame (centered at frame center, not top-left)
 */
export function calculateFrameWorldPositionsFromOffsetMap(
  offsetMap: OffsetMap,
  totalFrames: number,
  frameWidth?: number,
  frameHeight?: number
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  
  // Frame 0 centered at world origin (0, 0)
  // Start at top-left relative position: (-frameWidth/2, -frameHeight/2)
  // This way when we add half-dimensions, Frame 0's center ends up at (0, 0)
  const frame0X = frameWidth ? -frameWidth / 2 : 0
  const frame0Y = frameHeight ? -frameHeight / 2 : 0
  
  positions.push({
    frameIndex: 0,
    worldX: frame0X,
    worldY: frame0Y
  })
  
  // Calculate cumulative positions for each subsequent frame
  for (let i = 1; i < totalFrames; i++) {
    const prevPosition = positions[i - 1]
    
    // Get sequential offset from previous frame to this frame
    const offsetKey = `${i - 1}_${i}`
    const offset = offsetMap.all_pairs?.[offsetKey] || offsetMap.sequential?.[offsetKey]
    
    if (!offset) {
      positions.push({
        frameIndex: i,
        worldX: prevPosition.worldX,
        worldY: prevPosition.worldY
      })
      continue
    }
    
    // Add offset to previous position to get absolute world position
    positions.push({
      frameIndex: i,
      worldX: prevPosition.worldX + offset.dx,
      worldY: prevPosition.worldY + offset.dy
    })
  }
  
  // Apply center offset if frame dimensions provided
  // This shifts all positions so each frame's center is at its stored world position
  if (frameWidth && frameHeight) {
    const centerOffsetX = frameWidth / 2
    const centerOffsetY = frameHeight / 2
    positions.forEach(pos => {
      pos.worldX += centerOffsetX
      pos.worldY += centerOffsetY
    })
  }
  
  // Calculate world space bounds
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX))
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY))
  
  console.log(`[WorldSpace] Calculated ${totalFrames} positions: x=${(maxX - minX).toFixed(0)}px, y=${(maxY - minY).toFixed(0)}px`)
  
  return positions
}

/**
 * Calculate world space positions for all frames using manifest.json data
 * 
 * This is the manifest-based version of calculateFrameWorldPositions.
 * Uses quality-filtered neighbor offsets from manifest.json instead of full offset_map.json.
 * Produces identical results to the OffsetMap version when manifest contains same pairs.
 * 
 * @param manifest - Manifest containing quality-filtered neighbor offsets
 * @param frameWidth - Width of each frame (optional, for centering)
 * @param frameHeight - Height of each frame (optional, for centering)
 * @returns Array of world positions for each frame (centered at frame center, not top-left)
 */
export function calculateFrameWorldPositionsFromManifest(
  manifest: Manifest,
  frameWidth?: number,
  frameHeight?: number
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  const totalFrames = manifest.frames.length
  
  console.log(`[calculateFrameWorldPositionsFromManifest] Starting: ${totalFrames} frames, dimensions=${frameWidth}x${frameHeight}`)
  
  // Extract all_pairs from manifest (quality-filtered pairs only)
  const allPairs = extractOffsetsFromManifest(manifest)
  
  // Frame 0 centered at world origin (0, 0)
  // Start at top-left relative position: (-frameWidth/2, -frameHeight/2)
  // This way when we add half-dimensions, Frame 0's center ends up at (0, 0)
  const frame0X = frameWidth ? -frameWidth / 2 : 0
  const frame0Y = frameHeight ? -frameHeight / 2 : 0
  
  positions.push({
    frameIndex: 0,
    worldX: frame0X,
    worldY: frame0Y
  })
  
  // Calculate cumulative positions for each subsequent frame
  // Same sequential logic as OffsetMap version
  for (let i = 1; i < totalFrames; i++) {
    const prevPosition = positions[i - 1]
    
    // Get sequential offset from previous frame to this frame
    const offsetKey = `${i - 1}_${i}`
    const offset = allPairs.get(offsetKey)
    
    if (!offset) {
      // No sequential offset found - stay at previous position
      positions.push({
        frameIndex: i,
        worldX: prevPosition.worldX,
        worldY: prevPosition.worldY
      })
      continue
    }
    
    // Add offset to previous position to get absolute world position
    // Exact same calculation as OffsetMap version
    positions.push({
      frameIndex: i,
      worldX: prevPosition.worldX + offset.dx,
      worldY: prevPosition.worldY + offset.dy
    })
  }
  
  // Apply center offset if frame dimensions provided
  // This shifts all positions so each frame's center is at its stored world position
  if (frameWidth && frameHeight) {
    const centerOffsetX = frameWidth / 2
    const centerOffsetY = frameHeight / 2
    positions.forEach(pos => {
      pos.worldX += centerOffsetX
      pos.worldY += centerOffsetY
    })
  }
  
  // Calculate world space bounds
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX))
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY))
  
  const spanX = (maxX - minX).toFixed(0)
  const spanY = (maxY - minY).toFixed(0)
  console.log(`[WorldSpace] ✅ Calculated ${totalFrames} positions from manifest`)
  console.log(`   📐 Bounds: x=${spanX}px, y=${spanY}px`)
  console.log(`   📍 First frame: (${positions[0].worldX.toFixed(0)}, ${positions[0].worldY.toFixed(0)})`)
  console.log(`   📍 Last frame: (${positions[totalFrames-1].worldX.toFixed(0)}, ${positions[totalFrames-1].worldY.toFixed(0)})`)
  
  return positions
}

/**
 * Get world bounds (min/max X and Y) for all frames
 */
export function getWorldBounds(positions: FrameWorldPosition[], frameWidth: number, frameHeight: number) {
  if (positions.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 }
  }
  
  const minX = Math.min(...positions.map(p => p.worldX))
  const maxX = Math.max(...positions.map(p => p.worldX)) + frameWidth
  const minY = Math.min(...positions.map(p => p.worldY))
  const maxY = Math.max(...positions.map(p => p.worldY)) + frameHeight
  
  return { minX, maxX, minY, maxY }
}

/**
 * Calculate anchored frame positions relative to a selected frame
 * 
 * Instead of cumulative offsets from frame 0, each frame is positioned
 * at its direct offset from the selected frame. This allows spatial
 * relationship visualization when changing the selected frame.
 * 
 * @param selectedFrameIndex - The frame to use as anchor (0,0)
 * @param offsetMap - Pre-calculated offset map with all frame pairs
 * @param totalFrames - Total number of frames
 * @param cumulativePositions - Optional: fallback positions if direct offset not found
 * @returns Array of anchored positions with selected frame at (0,0)
 */
export function calculateAnchoredFramePositions(
  selectedFrameIndex: number,
  offsetMap: OffsetMap,
  totalFrames: number,
  cumulativePositions?: FrameWorldPosition[]
): FrameWorldPosition[] {
  const positions: FrameWorldPosition[] = []
  
  // Selected frame is always at origin
  positions.push({
    frameIndex: selectedFrameIndex,
    worldX: 0,
    worldY: 0
  })
  
  // For each other frame, calculate direct offset from selected frame
  for (let i = 0; i < totalFrames; i++) {
    if (i === selectedFrameIndex) {
      continue // Already added
    }
    
    // Look up direct offset from selected frame to this frame
    const offsetKey = `${selectedFrameIndex}_${i}`
    const offset = offsetMap.all_pairs?.[offsetKey]
    
    if (offset) {
      // Direct offset found - use it
      positions.push({
        frameIndex: i,
        worldX: offset.dx,
        worldY: offset.dy
      })
    } else {
      // Fallback: calculate from cumulative positions if available
      if (cumulativePositions && cumulativePositions.length > Math.max(selectedFrameIndex, i)) {
        const selectedPos = cumulativePositions[selectedFrameIndex]
        const targetPos = cumulativePositions[i]
        
        positions.push({
          frameIndex: i,
          worldX: targetPos.worldX - selectedPos.worldX,
          worldY: targetPos.worldY - selectedPos.worldY
        })
      } else {
        // No offset found and no cumulative fallback - skip this frame
        console.warn(`⚠️  [calculateAnchoredPositions] No offset found for pair ${offsetKey} and no cumulative fallback`)
        positions.push({
          frameIndex: i,
          worldX: 0,
          worldY: 0
        })
      }
    }
  }
  
  // Sort by frame index for consistent ordering
  positions.sort((a, b) => a.frameIndex - b.frameIndex)
  
  console.log(`[AnchoredPositions] Calculated positions anchored to frame ${selectedFrameIndex}: ${positions.length} frames`)
  
  return positions
}
